import React, { useState, useEffect } from 'react';
import { Button, Form, Card, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { Save, X, Package, Tag, ClipboardList, ArrowUp, ArrowDown } from 'lucide-react';
import useForm from '../../hooks/useForm';
import { adjustStock, createStockMovement } from '../../api/stock';
import { getAllProducts } from '../../api/products';

const StockAdjustmentForm = ({ onCancel, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productSearchTerm, setProductSearchTerm] = useState('');

    const initialValues = {
        productId: '',
        movementType: 'STOCK_IN',
        quantity: 1,
        reason: ''
    };

    const { values, handleChange, errors, validateForm, setValues } = useForm(initialValues);

    useEffect(() => {
        // Fetch products
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const data = await getAllProducts();
                // Trier par numeroProduit en ordre croissant
                const sortedData = [...data].sort((a, b) => {
                    const numA = Number(a.numeroProduit);
                    const numB = Number(b.numeroProduit);
                    return numA - numB;
                });
                setProducts(sortedData);
            } catch (err) {
                setError('Failed to load products. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    useEffect(() => {
        // Update selected product when productId changes
        if (values.productId) {
            const product = products.find(p => p.id === Number(values.productId));
            setSelectedProduct(product);
        } else {
            setSelectedProduct(null);
        }
    }, [values.productId, products]);

    // Fonction pour trouver un produit par nom, SKU ou numéro
    const findProduct = (searchTerm) => {
        if (!searchTerm) return null;

        const trimmedTerm = searchTerm.trim().toLowerCase();

        return products.find(product => {
            // Recherche par nom exact
            if (product.name.toLowerCase() === trimmedTerm) {
                return true;
            }

            // Recherche par SKU exact
            if (product.sku.toLowerCase() === trimmedTerm) {
                return true;
            }

            // Recherche par numéro de produit exact
            if (product.numeroProduit && product.numeroProduit.toString() === trimmedTerm) {
                return true;
            }

            // Recherche partielle dans le nom
            if (product.name.toLowerCase().includes(trimmedTerm)) {
                return true;
            }

            // Recherche partielle dans le SKU
            if (product.sku.toLowerCase().includes(trimmedTerm)) {
                return true;
            }

            return false;
        });
    };

    // Fonction pour extraire les informations du produit depuis la sélection datalist
    const extractProductInfo = (selectedText) => {
        const patterns = [
            // Format avec numéro : "123 - Nom - SKU (Stock: X)"
            /^(\d+)\s*-\s*(.+?)\s*-\s*([^\(]+)(?:\s*\(Stock:\s*\d+\))?$/,
            // Format standard : "Nom - SKU (Stock: X)"
            /^(.+?)\s*-\s*([^\(]+)(?:\s*\(Stock:\s*\d+\))?$/,
            // Format simple
            /^(.+)$/
        ];

        for (const pattern of patterns) {
            const match = selectedText.match(pattern);
            if (match) {
                if (match.length === 4) { // Format avec numéro
                    return {
                        numero: match[1].trim(),
                        name: match[2].trim(),
                        sku: match[3].trim()
                    };
                } else if (match.length === 3) { // Format standard
                    return {
                        name: match[1].trim(),
                        sku: match[2].trim()
                    };
                } else { // Format simple
                    return {
                        searchTerm: match[1].trim()
                    };
                }
            }
        }

        return { searchTerm: selectedText.trim() };
    };

    // Gestionnaire pour la recherche de produit
    const handleProductSearch = (searchValue) => {
        setProductSearchTerm(searchValue);

        // Si l'utilisateur a sélectionné une option du datalist
        if (searchValue.includes(' - ') || searchValue.match(/^\d+$/)) {
            const productInfo = extractProductInfo(searchValue);
            let foundProduct = null;

            if (productInfo.numero) {
                // Recherche prioritaire par numéro
                foundProduct = products.find(p =>
                    p.numeroProduit && p.numeroProduit.toString() === productInfo.numero
                );
            }

            if (!foundProduct && productInfo.sku) {
                // Recherche par SKU
                foundProduct = products.find(p =>
                    p.sku.toLowerCase() === productInfo.sku.toLowerCase()
                );
            }

            if (!foundProduct && productInfo.name) {
                // Recherche par nom
                foundProduct = products.find(p =>
                    p.name.toLowerCase() === productInfo.name.toLowerCase()
                );
            }

            if (!foundProduct && productInfo.searchTerm) {
                // Recherche générale
                foundProduct = findProduct(productInfo.searchTerm);
            }

            if (foundProduct) {
                setValues(prev => ({
                    ...prev,
                    productId: foundProduct.id.toString()
                }));
            } else {
                setValues(prev => ({
                    ...prev,
                    productId: ''
                }));
            }
        } else {
            // Recherche en temps réel sans sélection automatique
            const foundProduct = findProduct(searchValue);
            if (!foundProduct || searchValue.length <= 2) {
                setValues(prev => ({
                    ...prev,
                    productId: ''
                }));
            }
        }
    };

    const validationSchema = {
        productId: {
            required: true,
            errorMessage: 'Le produit est requis'
        },
        quantity: {
            required: true,
            min: 1,
            errorMessage: 'la quantité doit etre supérieue ou égale à 1'
        },
        reason: {
            required: true,
            errorMessage: 'La raison est requise'
        }
    };

    const handleDirectAdjustment = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Validate form
        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        if (!selectedProduct) {
            setError('Veuillez selectionner un produit valide');
            setLoading(false);
            return;
        }

        try {
            const newQuantity = Number(values.quantity);

            await adjustStock(Number(values.productId), newQuantity, values.reason);

            setSuccess('Ajustement de stock ajouté avec succès!');

            // Call the success callback after a short delay
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);
        } catch (err) {
            setError(err.message || 'Erreur lors de l ajustement de stock, veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleMovement = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Validate form
        const isValid = validateForm({
            ...validationSchema,
            movementType: {
                required: true,
                errorMessage: 'Le type du mouvement est requis'
            }
        });

        if (!isValid) {
            setLoading(false);
            return;
        }

        if (!selectedProduct) {
            setError('Veuillez selectionner un produit valide');
            setLoading(false);
            return;
        }

        // For STOCK_OUT, check if there's enough inventory
        if (values.movementType === 'STOCK_OUT' && selectedProduct.stockQuantity < Number(values.quantity)) {
            setError(`Not enough stock. Available: ${selectedProduct.stockQuantity}`);
            setLoading(false);
            return;
        }

        try {
            await createStockMovement(
                Number(values.productId),
                values.movementType,
                Number(values.quantity),
                values.reason
            );

            setSuccess('Le mouvement de stock a bien été enregistré');

            // Call the success callback after a short delay
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);
        } catch (err) {
            setError(err.message || 'Échec de l enregistrement du mouvement de stock. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">Gestion Stock</h5>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {/* Affichage du produit sélectionné */}
                {selectedProduct && (
                    <Alert variant="info" className="mb-3">
                        <strong>Produit sélectionné :</strong> {selectedProduct.name} - {selectedProduct.sku}
                        <br />
                        <strong>Stock actuel :</strong> {selectedProduct.stockQuantity} unités
                        {selectedProduct.numeroProduit && (
                            <>
                                <br />
                                <strong>Numéro :</strong> {selectedProduct.numeroProduit}
                            </>
                        )}
                    </Alert>
                )}

                <Row>
                    <Col md={6}>
                        <Card className="mb-3">
                            <Card.Header className="bg-light">
                                <h6 className="mb-0">Mouvements de stock</h6>
                            </Card.Header>
                            <Card.Body>
                                <Form onSubmit={handleMovement}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Produit</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <Package size={18} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                value={productSearchTerm}
                                                onChange={(e) => handleProductSearch(e.target.value)}
                                                list="products-list-movement"
                                                placeholder="Nom, numéro ou référence du produit"
                                                required
                                            />
                                            <datalist id="products-list-movement">
                                                {products.map(product => (
                                                    <option
                                                        key={product.id}
                                                        value={product.numeroProduit ? `${product.numeroProduit || ''} - ${product.name} - ${product.sku} (Stock: ${product.stockQuantity})` : `${product.name} - ${product.sku} (Stock: ${product.stockQuantity})`}
                                                    />
                                                ))}
                                            </datalist>
                                        </InputGroup>
                                        {errors.productId && <Form.Text className="text-danger">{errors.productId}</Form.Text>}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Type de mouvement</Form.Label>
                                        <div>
                                            <Form.Check
                                                inline
                                                type="radio"
                                                id="movement-in"
                                                name="movementType"
                                                value="STOCK_IN"
                                                checked={values.movementType === 'STOCK_IN'}
                                                onChange={handleChange}
                                                label={
                                                    <span>
                                                        <ArrowUp size={16} className="text-success me-1" />
                                                        Entrée de stock
                                                    </span>
                                                }
                                            />
                                            <Form.Check
                                                inline
                                                type="radio"
                                                id="movement-out"
                                                name="movementType"
                                                value="STOCK_OUT"
                                                checked={values.movementType === 'STOCK_OUT'}
                                                onChange={handleChange}
                                                label={
                                                    <span>
                                                        <ArrowDown size={16} className="text-danger me-1" />
                                                        Sortie de stock
                                                    </span>
                                                }
                                            />
                                            <Form.Check
                                                inline
                                                type="radio"
                                                id="movement-return"
                                                name="movementType"
                                                value="RETURN"
                                                checked={values.movementType === 'RETURN'}
                                                onChange={handleChange}
                                                label={
                                                    <span>
                                                        <ArrowUp size={16} className="text-info me-1" />
                                                        Retours
                                                    </span>
                                                }
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Quantité</Form.Label>
                                        <Form.Control
                                            type="number"
                                            onWheel={(e) => e.target.blur()}
                                            name="quantity"
                                            value={values.quantity}
                                            onChange={handleChange}
                                            min="1"
                                            required
                                        />
                                        {errors.quantity && <Form.Text className="text-danger">{errors.quantity}</Form.Text>}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Motif</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <ClipboardList size={18} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                name="reason"
                                                value={values.reason}
                                                onChange={handleChange}
                                                placeholder="Motif de ce mouvemement"
                                                required
                                            />
                                        </InputGroup>
                                        {errors.reason && <Form.Text className="text-danger">{errors.reason}</Form.Text>}
                                    </Form.Group>

                                    <div className="d-grid">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={loading || !selectedProduct}
                                        >
                                            {loading ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            ) : (
                                                <>
                                                    <Save size={16} className="me-1" />
                                                    Enregistrer mouvement
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card>
                            <Card.Header className="bg-light">
                                <h6 className="mb-0">Ajustement direct du stock</h6>
                            </Card.Header>
                            <Card.Body>
                                <Form onSubmit={handleDirectAdjustment}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Produit</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <Package size={18} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                value={productSearchTerm}
                                                onChange={(e) => handleProductSearch(e.target.value)}
                                                list="products-list-adjustment"
                                                placeholder="Nom, numéro ou référence du produit"
                                                required
                                            />
                                            <datalist id="products-list-adjustment">
                                                {products.map(product => (
                                                    <option
                                                        key={product.id}
                                                        value={`${product.numeroProduit || ''} - ${product.name} - ${product.sku} (Stock: ${product.stockQuantity})`}
                                                    />
                                                ))}
                                            </datalist>
                                        </InputGroup>
                                        {errors.productId && <Form.Text className="text-danger">{errors.productId}</Form.Text>}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouvelle quantité en stock</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="quantity"
                                            onWheel={(e) => e.target.blur()}
                                            value={values.quantity}
                                            onChange={handleChange}
                                            min="0"
                                            required
                                        />
                                        <Form.Text className="text-muted">
                                            Cela définira directement le niveau de stock à cette valeur
                                            {selectedProduct && (
                                                <>
                                                    <br />
                                                    <strong>Stock actuel :</strong> {selectedProduct.stockQuantity} → <strong>Nouveau stock :</strong> {values.quantity}
                                                </>
                                            )}
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Motif de l'ajustement</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <ClipboardList size={18} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                name="reason"
                                                value={values.reason}
                                                onChange={handleChange}
                                                placeholder="Motif de ce mouvemement"
                                                required
                                            />
                                        </InputGroup>
                                        {errors.reason && <Form.Text className="text-danger">{errors.reason}</Form.Text>}
                                    </Form.Group>

                                    <div className="d-grid">
                                        <Button
                                            variant="warning"
                                            type="submit"
                                            disabled={loading || !selectedProduct}
                                        >
                                            {loading ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            ) : (
                                                <>
                                                    <Save size={16} className="me-1" />
                                                    Ajuster le Stock
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <div className="d-flex justify-content-end mt-4">
                    <Button
                        variant="outline-secondary"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        <X size={16} className="me-1" />
                        Fermer
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default StockAdjustmentForm;